import { omitKeys, subsetTypeHasKey } from "@hex-insights/core";
import { FormState, formStateHasField } from "@hex-insights/forms";
import { CourseSectionFilterInput, mergeFilterInputs } from "../../../Utilities";
import * as CourseSectionFilterFormValues from "./form-values";

export function countActiveStandardFilters<
	K extends keyof CourseSectionFilterFormValues.FilterFormValues = keyof CourseSectionFilterFormValues.FilterFormValues,
>(formValues: Pick<CourseSectionFilterFormValues.FilterFormValues, K>) {
	let numActive = 0;
	if (subsetTypeHasKey(formValues, "academicSubjectIDIn") && formValues.academicSubjectIDIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "classYearIDsIn") && formValues.classYearIDsIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "homeRoomSectionIDIn") && formValues.homeRoomSectionIDIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "academicTermIDIn") && formValues.academicTermIDIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "teacherIDsIn") && formValues.teacherIDsIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "studentIDsIn") && formValues.studentIDsIn.length > 0) {
		numActive++;
	}
	return numActive;
}

export function clearStandardFilters<
	K extends keyof CourseSectionFilterFormValues.FilterFormValues = keyof CourseSectionFilterFormValues.FilterFormValues,
>(formState: FormState<Pick<CourseSectionFilterFormValues.FilterFormValues, K>>) {
	if (formStateHasField(formState, "academicSubjectIDIn")) {
		formState.formClearFunctions.academicSubjectIDIn();
	}
	if (formStateHasField(formState, "classYearIDsIn")) {
		formState.formClearFunctions.classYearIDsIn();
	}
	if (formStateHasField(formState, "homeRoomSectionIDIn")) {
		formState.formClearFunctions.homeRoomSectionIDIn();
	}
	if (formStateHasField(formState, "academicTermIDIn")) {
		formState.formClearFunctions.academicTermIDIn();
	}
	if (formStateHasField(formState, "teacherIDsIn")) {
		formState.formClearFunctions.teacherIDsIn();
	}
	if (formStateHasField(formState, "studentIDsIn")) {
		formState.formClearFunctions.studentIDsIn();
	}
}

export function resetStandardFilters<
	K extends keyof CourseSectionFilterFormValues.FilterFormValues = keyof CourseSectionFilterFormValues.FilterFormValues,
>(
	formState: FormState<Pick<CourseSectionFilterFormValues.FilterFormValues, K>>,
	initial: CourseSectionFilterFormValues.FilterFormValues,
) {
	if (formStateHasField(formState, "academicSubjectIDIn")) {
		formState.formSetFunctions.academicSubjectIDIn(initial.academicSubjectIDIn);
	}
	if (formStateHasField(formState, "classYearIDsIn")) {
		formState.formSetFunctions.classYearIDsIn(initial.classYearIDsIn);
	}
	if (formStateHasField(formState, "homeRoomSectionIDIn")) {
		formState.formSetFunctions.homeRoomSectionIDIn(initial.homeRoomSectionIDIn);
	}
	if (formStateHasField(formState, "academicTermIDIn")) {
		formState.formSetFunctions.academicTermIDIn(initial.academicTermIDIn);
	}
	if (formStateHasField(formState, "teacherIDsIn")) {
		formState.formSetFunctions.teacherIDsIn(initial.teacherIDsIn);
	}
	if (formStateHasField(formState, "studentIDsIn")) {
		formState.formSetFunctions.studentIDsIn(initial.studentIDsIn);
	}
}

export function prepareFormValuesForConversion<
	K extends keyof CourseSectionFilterFormValues.FilterFormValues = keyof CourseSectionFilterFormValues.FilterFormValues,
>(formValues: Pick<CourseSectionFilterFormValues.FilterFormValues, K>) {
	return omitKeys(formValues, ["academicSubjectIDIn", "classYearIDsIn", "teacherIDsIn", "studentIDsIn"]) as Pick<
		CourseSectionFilterFormValues.FilterFormValues,
		Exclude<K, "academicSubjectIDIn" | "classYearIDsIn" | "teacherIDsIn" | "studentIDsIn">
	>;
}

export function toFilterInputs<
	K extends keyof CourseSectionFilterFormValues.FilterFormValues = keyof CourseSectionFilterFormValues.FilterFormValues,
>(
	formValues: Pick<CourseSectionFilterFormValues.FilterFormValues, K>,
	filterInput: CourseSectionFilterInput,
): CourseSectionFilterInput {
	const customFilterInputs: CourseSectionFilterInput[] = [];

	if (subsetTypeHasKey(formValues, "academicSubjectIDIn") && formValues.academicSubjectIDIn.length > 0) {
		customFilterInputs.push({ course: [{ academicSubjectIDIn: formValues.academicSubjectIDIn }] });
	}

	if (subsetTypeHasKey(formValues, "classYearIDsIn") && formValues.classYearIDsIn.length > 0) {
		customFilterInputs.push({ course: [{ classYears: [{ idIn: formValues.classYearIDsIn }] }] });
	}

	if (subsetTypeHasKey(formValues, "teacherIDsIn") && formValues.teacherIDsIn.length > 0) {
		customFilterInputs.push({ teacherCourseSectionEnrollments: [{ teacherIDIn: formValues.teacherIDsIn }] });
	}

	if (subsetTypeHasKey(formValues, "studentIDsIn") && formValues.studentIDsIn.length > 0) {
		customFilterInputs.push({ studentCourseSectionEnrollments: [{ studentIDIn: formValues.studentIDsIn }] });
	}

	return mergeFilterInputs(filterInput, ...customFilterInputs);
}
